import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-core-644060845776.us-central1.run.app';

const FinopsUploadCard = ({ getFirebaseToken, onUploadSuccess, onError, clusterId }) => {
  const { t } = useTranslation();
  const [uploadStatus, setUploadStatus] = useState('IDLE'); // 'IDLE' | 'UPLOADING' | 'ENQUEUED'
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileType, setFileType] = useState('K8S_MANIFEST'); // 'K8S_MANIFEST' | 'GCP_BILLING'
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadStatus !== 'UPLOADING') {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (uploadStatus === 'UPLOADING') return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndUpload(files[0]);
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const triggerFileInput = () => {
    if (uploadStatus !== 'UPLOADING' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateAndUpload = async (file) => {
    if (!file) return;
    const filename = file.name.toLowerCase();
    const isYaml = filename.endsWith('.yaml') || filename.endsWith('.yml');
    const isCsv = filename.endsWith('.csv');

    if (!isYaml && !isCsv) {
      if (onError) {
        onError(t('finops.error_invalid_extension') || 'Invalid file type. Only .yaml, .yml, and .csv are supported.');
      }
      return;
    }

    // Auto-adjust file type based on extension as helper
    const resolvedType = isCsv ? 'GCP_BILLING' : 'K8S_MANIFEST';
    setFileType(resolvedType);

    await uploadFile(file, resolvedType);
  };

  const uploadFile = async (file, resolvedFileType) => {
    setUploadStatus('UPLOADING');
    try {
      const token = getFirebaseToken ? await getFirebaseToken() : '';
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', resolvedFileType);
      if (clusterId && clusterId.trim()) {
        formData.append('cluster_id', clusterId.trim());
      }

      const headers = {
        'Content-Type': 'multipart/form-data'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(`${BACKEND_URL}/api/finops/audit`, formData, { headers });

      if (response.status === 202 || response.status === 200) {
        setUploadStatus('ENQUEUED');
        if (onUploadSuccess) {
          onUploadSuccess(response.data?.job_id);
        }
        // Reset to idle after 4 seconds
        setTimeout(() => {
          setUploadStatus('IDLE');
        }, 4000);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      console.error('Error uploading resource file:', err);
      setUploadStatus('IDLE');
      const apiError = err.response?.data?.error || err.message || 'Error uploading file';
      if (onError) {
        onError(apiError);
      }
    }
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: isDragOver ? '2px dashed var(--primary-color)' : '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '30px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    minWidth: 0
  };

  const dropZoneStyle = {
    background: isDragOver ? 'rgba(79, 70, 229, 0.08)' : 'rgba(0, 0, 0, 0.2)',
    border: '1px dashed var(--glass-border)',
    borderRadius: '12px',
    padding: '40px 20px',
    cursor: uploadStatus === 'UPLOADING' ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
  };

  const selectStyle = {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#E2E8F0',
    fontSize: '0.9rem',
    outline: 'none',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '320px',
    marginBottom: '20px',
    colorScheme: 'dark'
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 10px 0', fontSize: '1.25rem', fontWeight: '700', color: '#E2E8F0' }}>
        {t('finops.upload_title')}
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
        <label htmlFor="file-type-select" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '600' }}>
          {t('finops.file_type_label')}
        </label>
        <select
          id="file-type-select"
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          disabled={uploadStatus === 'UPLOADING'}
          style={selectStyle}
        >
          <option value="K8S_MANIFEST">{t('finops.file_type_k8s')}</option>
          <option value="GCP_BILLING">{t('finops.file_type_gcp')}</option>
        </select>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".yaml,.yml,.csv"
        style={{ display: 'none' }}
      />

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        style={dropZoneStyle}
      >
        {uploadStatus === 'IDLE' && (
          <>
            <span style={{ fontSize: '3rem' }}>📁</span>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#CBD5E1' }}>
              {t('finops.upload_drop_files')}
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('finops.upload_supports')}
            </p>
          </>
        )}

        {uploadStatus === 'UPLOADING' && (
          <>
            <span className="spinner" style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid rgba(79, 70, 229, 0.3)',
              borderTopColor: 'var(--primary-color)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#E2E8F0', fontWeight: '600' }}>
              {t('finops.status_uploading')}
            </p>
          </>
        )}

        {uploadStatus === 'ENQUEUED' && (
          <>
            <span style={{ fontSize: '3rem', color: '#10B981' }}>✔️</span>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#10B981', fontWeight: '700' }}>
              {t('finops.status_enqueued')}
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FinopsUploadCard;
